/**
 * SG-12652 §13.6 — cross-sign-out Stripe Checkout success marker persistence.
 *
 * Problem: a customer who spends >Sheetgo-session-TTL on Stripe Checkout
 * (entering card details, hesitating) has their BE session expire while
 * they're away. On return, Stripe redirects to `?checkout=success` and the
 * first authed API call returns 401. `signOutCallback` redirects the user
 * through /sign-in (per `services/api/index.js` + `LayoutHOC#handleSignOutAuth`),
 * which drops the URL search string. After re-signing-in the user lands on
 * their default page with no indication their purchase succeeded — Stripe-
 * side the sub was created and the webhook provisioned the plan, but the FE
 * celebration UX is silent.
 *
 * Fix: stash a success marker in sessionStorage at the EARLIEST possible
 * point on page load (before any React component mounts, before any API
 * call can fire `signOutCallback`). After the bounce + sign-in chain
 * settles, `StripeReturnHandler` mounts on the authed layout, finds the
 * marker, and surfaces the celebration dialog the same way a URL-marker
 * arrival would.
 *
 * Why sessionStorage (not localStorage):
 *   - The whole bounce-and-recovery chain happens in ONE tab. sessionStorage
 *     scopes the marker to that tab, so a parallel unrelated tab can't pick
 *     it up and pop a stale celebration.
 *   - Survives `window.location.replace` within the tab (the bounce uses
 *     this); cleared on tab close, which is the correct expiry.
 *
 * Why not a cookie:
 *   - Cookies travel to the server on every request — unnecessary overhead
 *     for a purely-FE recovery signal.
 *   - Cookies survive across tabs by default, reintroducing the stale-pop
 *     problem above.
 *
 * Capture at: `src/index.js` top-level (synchronous, before `render()`).
 * Consume at: `StripeReturnHandler` first render (URL marker takes
 *     precedence; sessionStorage is the fallback path).
 *
 * TTL: 30 minutes. A genuine bounce-and-sign-in chain settles in <1 min;
 * 30 min is generous slack for a customer who closes laptop mid-flow then
 * resumes. Past that we'd rather not pop a celebration for a stale event —
 * the user already saw their plan upgrade in the UI on prior visits.
 *
 * Known scope tradeoff: a different-user sign-in on the same tab within
 * the TTL window would also receive the celebration. Pinning to identity
 * isn't viable at capture time — Redux state isn't initialized at module
 * load and the customer's session may already be expired (the very
 * condition this fix recovers from). The dialog is cosmetic-only — no
 * privileged data is rendered, and the BE-provisioned plan is bound to
 * the actually-purchasing user via Stripe metadata, so a false-positive
 * pop is an annoyance, not a security or billing concern.
 */

const STORAGE_KEY = 'sheetgoStripeReturnMarker'
const TTL_MS = 30 * 60 * 1000

const MARKER_CHECKOUT_SUCCESS = 'checkout_success'

const SUPPORTED_MARKERS = new Set([MARKER_CHECKOUT_SUCCESS])


/**
 * Read `window.location.search` and persist any recognized Stripe return
 * marker to sessionStorage. Overwrites any existing marker on each
 * qualifying call — the latest crossing wins, which keeps the TTL window
 * extending from the most recent bounce.
 *
 * Safe to call at module load time — guards against missing window /
 * sessionStorage (SSR / test environments). Never throws.
 *
 * Scope today: only `?checkout=success` is captured. `?checkout=cancel`
 * is a silent path and doesn't need cross-bounce recovery. `?portal=return`
 * has its own pre-portal snapshot (see `portalSnapshot.js`) that already
 * survives sessionStorage; adding a sibling marker here would duplicate
 * that mechanism. Extend `SUPPORTED_MARKERS` when a future requirement
 * demands it.
 *
 * @returns {void}
 */
export function captureStripeReturnMarker() {
    if (typeof window === 'undefined') return
    if (typeof sessionStorage === 'undefined') return
    let search
    try {
        search = window.location && window.location.search
    } catch (e) {
        return
    }
    if (!search) return
    let params
    try {
        params = new URLSearchParams(search)
    } catch (e) {
        return
    }
    if (params.get('checkout') !== 'success') return
    const marker = {
        type: MARKER_CHECKOUT_SUCCESS,
        ts: Date.now()
    }
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(marker))
    } catch (e) {
        // Private browsing / quota — silent failure is acceptable; the
        // worst case is the bounce-and-recovery path falls back to its
        // pre-§13.6 behavior (silent landing on default page).
    }
}


/**
 * Read the marker without clearing it. Returns null when missing,
 * malformed, expired, or sessionStorage is unavailable.
 *
 * @returns {{type: string, ts: number}|null}
 */
export function peekStripeReturnMarker() {
    if (typeof sessionStorage === 'undefined') return null
    let raw
    try {
        raw = sessionStorage.getItem(STORAGE_KEY)
    } catch (e) {
        return null
    }
    if (!raw) return null
    let parsed
    try {
        parsed = JSON.parse(raw)
    } catch (e) {
        // Garbage in storage — clear so we don't keep tripping on it.
        clearStripeReturnMarker()
        return null
    }
    if (!parsed || typeof parsed.ts !== 'number' || !isFinite(parsed.ts)) {
        // Defensive removal so we don't keep tripping on garbage.
        clearStripeReturnMarker()
        return null
    }
    // Future timestamps (clock skew or manual tampering) would extend the
    // TTL window indefinitely — reject and clear.
    if (parsed.ts > Date.now()) {
        clearStripeReturnMarker()
        return null
    }
    if (Date.now() - parsed.ts > TTL_MS) {
        clearStripeReturnMarker()
        return null
    }
    if (!SUPPORTED_MARKERS.has(parsed.type)) {
        clearStripeReturnMarker()
        return null
    }
    return parsed
}


/**
 * Read AND clear the marker atomically (best-effort single-shot).
 * Returns the same shape as `peekStripeReturnMarker`.
 *
 * @returns {{type: string, ts: number}|null}
 */
export function consumeStripeReturnMarker() {
    const marker = peekStripeReturnMarker()
    if (marker) clearStripeReturnMarker()
    return marker
}


/**
 * Idempotent clear. Safe to call when no marker is present.
 *
 * @returns {void}
 */
export function clearStripeReturnMarker() {
    if (typeof sessionStorage === 'undefined') return
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch (e) {
        // Best-effort.
    }
}


// Exported for tests + downstream import symmetry with the URL constants.
export const MARKER_TYPES = {
    CHECKOUT_SUCCESS: MARKER_CHECKOUT_SUCCESS
}
